import { useCallback, useMemo, useState } from 'react'
import { SkeletonList } from '../componentes/Skeleton'
import { EstadoError } from '../componentes/EstadoError'
import { EstadoVacio } from '../componentes/EstadoVacio'
import { Modal } from '../componentes/Modal'
import { PerfilCliente } from '../componentes/PerfilCliente'
import { ConmutadorModo } from './agendamiento/ConmutadorModo'
import { useFiltrosAgendamiento, type Modo } from '../hooks/useFiltrosAgendamiento'
import { BarraPublicar } from './agendamiento/BarraPublicar'
import { BarraSeleccion } from './agendamiento/BarraSeleccion'
import { bonosDelCatalogo } from './agendamiento/catalogo'
import {
  TAMANO_PAGINA,
  useCatalogo,
  useHistoriales,
  usePublicar,
  useResolver,
  useSincronizarClientes,
  useTablaClientes,
} from './agendamiento/consultas'
import { IconoActualizar } from '../componentes/iconos'
import { DetallePedido } from './agendamiento/DetallePedido'
import type {
  FichasElegidas,
  FilaAgendamiento,
  LineasAnadidas,
  PreciosEditados,
  CantidadesEditadas,
  BonosElegidos,
  LineasEliminadas,
  DireccionEntregaElegida,
} from './agendamiento/filas'
import {
  aEntradasPublicar,
  clavePrecio,
  lineasDe,
  motivoNoPublicable,
  partnerElegido,
  publicables,
} from './agendamiento/filas'
import { FiltrosTabla } from './agendamiento/FiltrosTabla'
import { ModoIndividual } from './agendamiento/ModoIndividual'
import { PanelReparto, type ExtraElegido } from './agendamiento/PanelReparto'
import type { DiaRuta } from '../hooks/useFiltrosAgendamiento'
import { ModoMasivo } from './agendamiento/ModoMasivo'
import { PanelResultados, REINTENTABLES } from './agendamiento/PanelResultados'
import { FichaResuelta, SelectorFicha } from './agendamiento/SelectorFicha'
import type { FilaTabla } from './agendamiento/tabla'
import {
  DIAS,
  SEMANAS,
  aEntradaResolver,
  bonoDelHabitual,
  filasDeTabla,
  sinBono,
} from './agendamiento/tabla'
import { TablaClientes } from './agendamiento/TablaClientes'
import { TablaResolucion } from './agendamiento/TablaResolucion'

/**
 * Agendar repartos. Sustituye a la herramienta `Agendamiento masivo admin` de
 * n8n: al publicar, el backend crea el presupuesto en Odoo en borrador y
 * dispara el webhook que crea el evento de calendario.
 *
 * **La tabla es la pantalla.** El trabajo de un dia cualquiera es "quien reparte
 * el martes": se elige el dia, salen sus clientes con su pedido de siempre ya
 * puesto, se marcan los que van y se publica. Antes eso empezaba en un cuadro de
 * texto donde habia que pegar una lista sacada de otro sitio, que es pedirle al
 * administrador que haga a mano la consulta que la base de datos sabe hacer.
 *
 * Pegar la lista **no se ha quitado**: funciona, hay quien trabaja asi, y sigue
 * ahi detras de un enlace. Lo mismo el alta suelta, que es el unico camino que
 * admite extras -dispensador, fianza, portes-.
 *
 * Los tres caminos se juntan en la revision: de ahi en adelante comparten la
 * eleccion de ficha, la comparacion de precios y el boton de publicar. Duplicar
 * ese tramo habria significado tres sitios donde olvidarse de comprobar que la
 * ficha esta elegida, que es la comprobacion que evita mandar un pedido a otro
 * cliente.
 *
 * El estado compartible -dia de ruta, semana, busqueda, fecha- vive en la url.
 * Las casillas marcadas no: un enlace que reconstruyera treinta clientes listos
 * para publicar seria un enlace peligroso.
 */

const CLASE_PAGINACION =
  'rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 font-medium text-tinta-2 transition-colors hover:bg-elevado disabled:cursor-not-allowed disabled:border-borde disabled:text-desactivado-tinta'

export function Agendamiento() {
  const {
    filtros,
    cambiarModo,
    cambiarFecha,
    cambiarBusqueda,
    elegirCliente,
    cambiarRuta,
    cambiarSemana,
    irAPagina,
  } = useFiltrosAgendamiento()

  const [filas, setFilas] = useState<FilaAgendamiento[]>([])
  const [fichas, setFichas] = useState<FichasElegidas>({})
  const [precios, setPrecios] = useState<PreciosEditados>({})
  // Los productos que el administrador le suma al pedido de un cliente
  // concreto, ya en la revision. Es lo que permite agendar a diez clientes su
  // bono de siempre y a uno de ellos, ademas, un dispensador.
  const [anadidas, setAnadidas] = useState<LineasAnadidas>({})
  // Opcion A: cantidad y bono editados en la propia linea del modal Revisar.
  // Indexados por `clave|indiceDeLinea`, igual que `precios`.
  const [cantidades, setCantidades] = useState<CantidadesEditadas>({})
  const [bonosLinea, setBonosLinea] = useState<BonosElegidos>({})
  const [eliminadas, setEliminadas] = useState<LineasEliminadas>({})
  // Multi-reparto: direcciones de entrega marcadas por fila (clave → direcciones).
  // Una fila con direcciones marcadas se publica como un albarán por cada una.
  const [direccionesPorFila, setDireccionesPorFila] = useState<Record<string, DireccionEntregaElegida[]>>({})
  const [seleccionada, setSeleccionada] = useState<string | null>(null)

  // Lo marcado se guarda con la fila entera y no solo con el id: asi cambiar de
  // pagina o de dia no pierde lo que ya estaba marcado, y la revision sabe el
  // nombre y el NIF de cada uno aunque su fila ya no este a la vista.
  const [marcados, setMarcados] = useState<Map<string, FilaTabla>>(() => new Map())
  const [bonosPorFila, setBonosPorFila] = useState<Record<string, string>>({})
  // Rediseño B.3: fila abierta en el panel lateral, extras (packs/sueltos) y día
  // de reparto por cliente. Van por contact_id para sobrevivir a la repaginación.
  const [filaAbierta, setFilaAbierta] = useState<string | null>(null)
  const [extrasPorCliente, setExtrasPorCliente] = useState<Record<string, ExtraElegido[]>>({})
  const [diaPorCliente, setDiaPorCliente] = useState<Record<string, DiaRuta | null>>({})
  // Perfil 360º abierto desde el nombre del cliente (clickeable en la tabla).
  const [perfilId, setPerfilId] = useState<string | null>(null)
  // Aviso cuando "Publicar" no tiene nada que enviar (antes era un return mudo).
  const [avisoPublicar, setAvisoPublicar] = useState<string | null>(null)

  const enTabla = filtros.modo === 'tabla'

  const tabla = useTablaClientes(
    { ruta: filtros.ruta, semana: filtros.semana, q: filtros.q, page: filtros.page },
    enTabla,
  )
  const catalogo = useCatalogo(enTabla)
  const resolverTabla = useResolver()
  const publicar = usePublicar()
  const [avisoSync, setAvisoSync] = useState<string | null>(null)
  const sync = useSincronizarClientes((r) => {
    setAvisoSync(
      r.ok
        ? `${r.totalHolded} clientes al día (${r.nuevos} nuevos). Ya puedes buscarlos.`
        : r.mensaje,
    )
  })

  // Una revision nueva borra las decisiones de la anterior: una ficha elegida
  // para el cliente de la fila 3 no significa nada en otra lista.
  const reiniciar = useCallback(
    (nuevas: FilaAgendamiento[]) => {
      setFilas(nuevas)
      setFichas({})
      setPrecios({})
      setAnadidas({})
      setCantidades({})
      setBonosLinea({})
      setEliminadas({})
      setSeleccionada(nuevas.length === 1 ? nuevas[0].clave : null)
      publicar.reset()
    },
    [publicar],
  )

  /**
   * Limpia la selección SIN resetear la mutación de publicar: así el modal de
   * revisión se cierra (filas vacías) pero publicar.data sobrevive para mostrar
   * el modal de resultado. (`reiniciar` sí resetea publicar, para empezar limpio.)
   */
  const limpiarSeleccion = useCallback(() => {
    setFilas([])
    setFichas({})
    setPrecios({})
    setAnadidas({})
    setCantidades({})
    setBonosLinea({})
    setEliminadas({})
    setSeleccionada(null)
  }, [])

  const contactIds = useMemo(
    () =>
      [
        ...new Set(
          filas
            .map((fila) => fila.resultado?.cliente?.contact_id)
            .filter((id): id is string => Boolean(id)),
        ),
      ].sort(),
    [filas],
  )
  const historiales = useHistoriales(contactIds)

  const listas = publicables(filas, fichas, anadidas)
  const detalle = filas.find((fila) => fila.clave === seleccionada) ?? null

  // Estables: son dependencias de las columnas memoizadas de la tabla, y una
  // funcion nueva por render las reconstruiria en cada pulsacion.
  const seleccionar = useCallback(
    (clave: string) => setSeleccionada((previa) => (previa === clave ? null : clave)),
    [],
  )

  const elegirFicha = useCallback((clave: string, id: number | null) => {
    setFichas((previas) => {
      if (id === null) {
        const { [clave]: _descartada, ...resto } = previas
        return resto
      }
      return { ...previas, [clave]: id }
    })
  }, [])

  // --- La tabla de clientes -------------------------------------------------

  const productos = useMemo(() => catalogo.data?.items ?? [], [catalogo.data])
  const bonos = useMemo(() => bonosDelCatalogo(productos), [productos])
  const filasTabla = useMemo(() => tabla.data?.items ?? [], [tabla.data])
  const clavesMarcadas = useMemo(() => new Set(marcados.keys()), [marcados])

  /**
   * El bono de una fila: el que haya elegido el administrador manda sobre el que
   * dedujo el backend del ultimo pedido. Una cadena vacia guardada es una
   * decision -"ninguno"- y por eso se distingue de "no ha tocado nada".
   */
  const bonoDeFila = useCallback(
    (fila: FilaTabla) => {
      const elegido = bonosPorFila[fila.cliente.contact_id]
      if (elegido !== undefined) {
        return bonos.find((bono) => String(bono.id) === elegido) ?? null
      }
      return bonoDelHabitual(bonos, fila.pedido_habitual)
    },
    [bonos, bonosPorFila],
  )

  const alternarMarca = useCallback((fila: FilaTabla) => {
    setMarcados((previos) => {
      const siguientes = new Map(previos)
      if (siguientes.has(fila.cliente.contact_id)) siguientes.delete(fila.cliente.contact_id)
      else siguientes.set(fila.cliente.contact_id, fila)
      return siguientes
    })
  }, [])

  /** Solo los de la pagina que se esta viendo: "todos" no puede alcanzar a los que el filtro dejo fuera. */
  const marcarVisibles = useCallback(
    (marcar: boolean) => {
      setMarcados((previos) => {
        const siguientes = new Map(previos)
        for (const fila of filasTabla) {
          if (marcar) siguientes.set(fila.cliente.contact_id, fila)
          else siguientes.delete(fila.cliente.contact_id)
        }
        return siguientes
      })
    },
    [filasTabla],
  )

  const elegirBono = useCallback((contactId: string, productoId: string) => {
    setBonosPorFila((previos) => ({ ...previos, [contactId]: productoId }))
  }, [])

  // Rediseño B.3: abrir/cerrar el panel lateral y guardar extras/día por cliente.
  const abrirFila = useCallback(
    (fila: FilaTabla) =>
      setFilaAbierta((previa) =>
        previa === fila.cliente.contact_id ? null : fila.cliente.contact_id,
      ),
    [],
  )
  const cambiarExtras = useCallback(
    (contactId: string, extras: ExtraElegido[]) =>
      setExtrasPorCliente((previos) => ({ ...previos, [contactId]: extras })),
    [],
  )
  const elegirDia = useCallback(
    (contactId: string, dia: DiaRuta | null) =>
      setDiaPorCliente((previos) => ({ ...previos, [contactId]: dia })),
    [],
  )

  const soltarMarcados = () => {
    setMarcados(new Map())
    setFilaAbierta(null)
    setExtrasPorCliente({})
    setDiaPorCliente({})
    reiniciar([])
  }

  const marcadas = [...marcados.values()]
  const faltanBono = marcadas.filter((fila) => sinBono(bonoDeFila(fila))).length

  /** Resuelve una lista concreta de filas y abre la revisión con sus extras sembrados. */
  const revisarLista = (filasARevisar: FilaTabla[]) => {
    if (filasARevisar.length === 0) return
    resolverTabla.mutate(
      { clientes: filasARevisar.map((fila) => aEntradaResolver(fila, bonoDeFila(fila))) },
      {
        onSuccess: (respuesta) => {
          const nuevas = filasDeTabla(filasARevisar, respuesta.resultados)
          reiniciar(nuevas)
          // Siembra las líneas añadidas con los extras (packs/sueltos) que el
          // administrador eligió en el panel lateral de cada cliente. La clave
          // de fila en la tabla es el contact_id.
          const sembradas: LineasAnadidas = {}
          for (const fila of nuevas) {
            const extras = extrasPorCliente[fila.clave]
            if (extras && extras.length > 0) {
              sembradas[fila.clave] = extras.map((e) => ({
                nombre: e.nombre,
                cantidad: e.cantidad,
                precio: e.precio,
                product_id: e.productoId,
              }))
            }
          }
          if (Object.keys(sembradas).length > 0) setAnadidas(sembradas)
        },
      },
    )
  }

  const revisarMarcados = () => revisarLista(marcadas)

  /**
   * Revisar y publicar DESDE el panel Configurar de un cliente: marca ese
   * cliente (si no lo estaba), cierra el panel y abre la revisión con él más
   * todos los que ya estuvieran marcados. Así no hace falta cerrar, marcar y
   * pulsar "Revisar" por separado.
   */
  const revisarDesdePanel = (fila: FilaTabla) => {
    const union = new Map(marcados)
    union.set(fila.cliente.contact_id, fila)
    setMarcados(union)
    setFilaAbierta(null)
    revisarLista([...union.values()])
  }

  const irAModo = (modo: Modo) => {
    cambiarModo(modo)
    setMarcados(new Map())
    setBonosPorFila({})
    setFilaAbierta(null)
    setExtrasPorCliente({})
    setDiaPorCliente({})
    reiniciar([])
  }

  // --- Publicar -------------------------------------------------------------

  const publicarFilas = (aPublicar: FilaAgendamiento[]) => {
    // La fecha que el usuario elige en el modal MANDA para todos: no se recoloca
    // al día de ruta de cada cliente (eso confundía — elegías 28 y salía el 26).
    const clientes = aPublicar
      .flatMap((fila) =>
        aEntradasPublicar(
          fila, fichas, precios, anadidas, undefined, cantidades, bonosLinea, eliminadas,
          direccionesPorFila[fila.resultado?.cliente?.contact_id ?? ''] ?? [],
        ),
      )
      .filter((entrada) => entrada !== null)
    // Nunca fallar en silencio: si no hay fecha o nada publicable, dilo.
    if (filtros.fecha === '') {
      setAvisoPublicar('Elige la fecha de reparto antes de publicar.')
      return
    }
    if (clientes.length === 0) {
      const motivo = aPublicar
        .map((fila) => motivoNoPublicable(fila, fichas, anadidas))
        .find((m) => m !== null)
      setAvisoPublicar(
        motivo
          ? `No se puede publicar todavía: ${motivo}`
          : 'No hay ninguna línea de pedido que publicar.',
      )
      return
    }
    setAvisoPublicar(null)
    publicar.mutate(
      { fecha_reparto: filtros.fecha, clientes },
      {
        // Si todo salió bien, cierra el modal de revisión (vacía la selección)
        // para que el modal de RESULTADO quede solo. Si quedaron clientes
        // reintentables, MANTÉN las filas: el botón "Reintentar" las necesita.
        // limpiarSeleccion NO resetea la mutación → publicar.data sobrevive.
        onSuccess: (data) => {
          const hayPendientes = (data.resultados ?? []).some((r) =>
            REINTENTABLES.has(r.estado),
          )
          if (!hayPendientes) limpiarSeleccion()
        },
      },
    )
  }

  const reintentar = () => {
    const pendientes = new Set(
      (publicar.data?.resultados ?? [])
        .filter((resultado) => REINTENTABLES.has(resultado.estado))
        .map((resultado) => resultado.contact_id),
    )
    publicarFilas(
      listas.filter((fila) => pendientes.has(fila.resultado?.cliente?.contact_id ?? '')),
    )
  }

  const total = tabla.data?.total ?? 0
  const tamano = tabla.data?.page_size || TAMANO_PAGINA
  const pagina = tabla.data?.page ?? filtros.page
  const paginas = Math.max(1, Math.ceil(total / tamano))

  // Los filtros activos, escritos como se leen en los botones: "el viernes" y
  // no "r5". El codigo es del backend y quien mira la pantalla no lo conoce.
  const activos: string[] = []
  if (filtros.q) activos.push(`la busqueda "${filtros.q}"`)
  if (filtros.ruta) {
    activos.push(`el ${DIAS.find((dia) => dia.clave === filtros.ruta)?.etiqueta.toLowerCase()}`)
  }
  if (filtros.semana) {
    activos.push(
      `la ${SEMANAS.find((semana) => semana.clave === filtros.semana)?.etiqueta.toLowerCase()}`,
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-semibold text-tinta">Agendamiento</h1>
          <p className="mt-1 text-sm text-tinta-apagada">
            Genera el albarán del reparto directamente en Holded y avisa a n8n para que ponga el evento en el
            calendario. El albarán queda emitido con la fecha real de reparto.
          </p>
        </div>
        {/* Red de seguridad: si un cliente está en Holded pero aún no aparece en
            la app, este botón lo trae al momento (la app también sincroniza sola
            2 veces al día). Alineado arriba a la derecha, visible en todos los modos. */}
        <div className="flex shrink-0 flex-col items-end gap-1 pt-0.5">
          <button
            type="button"
            onClick={() => {
              setAvisoSync(null)
              sync.mutate()
            }}
            disabled={sync.isPending}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-borde-fuerte bg-superficie px-3 py-1.5 text-sm font-medium text-tinta transition-colors hover:bg-elevado disabled:opacity-60"
          >
            <IconoActualizar aria-hidden="true" className={`size-4 ${sync.isPending ? 'animate-spin' : ''}`} />
            {sync.isPending ? 'Actualizando clientes…' : 'Actualizar clientes'}
          </button>
          {avisoSync ? (
            <span className={`max-w-[16rem] text-right text-xs ${sync.data?.ok === false ? 'text-critico-tinta' : 'text-tinta-apagada'}`}>
              {avisoSync}
            </span>
          ) : (
            <span className="max-w-[16rem] text-right text-[11px] text-tinta-apagada">
              ¿Falta un cliente recién creado en Holded? Púlsalo.
            </span>
          )}
        </div>
      </div>

      {/* Las tres formas de agendar (rediseño: antes dos enlaces grises al pie).
          La tabla es el modo por defecto; alta suelta añade extras; Excel importa. */}
      <ConmutadorModo modo={filtros.modo} onCambiar={irAModo} />

      {/* La fecha en modo tabla vive en la barra de filtros (rediseño A). En los
          otros modos (individual/masivo), que no tienen esa barra, sigue aquí. */}
      {!enTabla ? (
        <div>
          <label htmlFor="fecha-reparto" className="block text-sm font-medium text-tinta-2">
            Fecha de reparto
          </label>
          <input
            id="fecha-reparto"
            type="date"
            value={filtros.fecha}
            onChange={(evento) => cambiarFecha(evento.target.value)}
            className="mt-1 rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm text-tinta outline-none focus:border-foco focus:ring-2 focus:ring-foco-halo [color-scheme:dark]"
          />
          <p className="mt-1 text-xs text-tinta-apagada">
            El pedido se crea a las 08:00 de ese dia, que es por lo que filtra el reparto.
          </p>
        </div>
      ) : null}

      {enTabla ? (
        <section data-tour="agenda-tabla" aria-label="Clientes del reparto" className="flex min-w-0 flex-col gap-4">
          <FiltrosTabla
            filtros={filtros}
            fecha={filtros.fecha}
            onFecha={cambiarFecha}
            onRuta={cambiarRuta}
            onSemana={cambiarSemana}
            onBuscar={cambiarBusqueda}
          />

          {tabla.isPending ? (
            // Una pagina de 100 clientes tarda unos tres segundos: el skeleton
            // muestra la silueta de la tabla para que no parezca colgada.
            <SkeletonList />
          ) : tabla.isError ? (
            <EstadoError error={tabla.error} onReintentar={() => void tabla.refetch()} />
          ) : filasTabla.length === 0 ? (
            <EstadoVacio
              titulo="Ningun cliente con estos filtros"
              descripcion={
                activos.length > 0
                  ? `No hay ningun cliente que case con ${activos.join(', ')}.`
                  : 'El listado ha llegado vacio de la API.'
              }
            />
          ) : (
            <>
              {tabla.isFetching ? (
                <p role="status" aria-live="polite" className="text-sm text-tinta-apagada">
                  Actualizando la tabla
                </p>
              ) : null}

              <TablaClientes
                filas={filasTabla}
                bonos={bonos}
                catalogoPendiente={catalogo.isPending}
                marcados={clavesMarcadas}
                bonoDeFila={bonoDeFila}
                onAlternar={alternarMarca}
                onMarcarVisibles={marcarVisibles}
                onElegirBono={elegirBono}
                onAbrirFila={abrirFila}
                filaAbierta={filaAbierta}
                onVerPerfil={setPerfilId}
              />

              {/* Panel de reparto B.3 ahora en MODAL (no panel lateral): la tabla
                  ocupa todo el ancho y configurar un cliente no mueve la página. */}
              {(() => {
                const abierta = filasTabla.find((f) => f.cliente.contact_id === filaAbierta)
                return abierta ? (
                  <PanelReparto
                    fila={abierta}
                    catalogo={productos}
                    catalogoPendiente={catalogo.isPending}
                    bonoElegido={bonoDeFila(abierta)}
                    extras={extrasPorCliente[abierta.cliente.contact_id] ?? []}
                    dia={diaPorCliente[abierta.cliente.contact_id] ?? null}
                    onElegirBono={elegirBono}
                    onCambiarExtras={cambiarExtras}
                    onElegirDia={elegirDia}
                    direccionesElegidas={direccionesPorFila[abierta.cliente.contact_id] ?? []}
                    onCambiarDirecciones={(dirs) =>
                      setDireccionesPorFila((prev) => ({ ...prev, [abierta.cliente.contact_id]: dirs }))
                    }
                    onCerrar={() => setFilaAbierta(null)}
                    onRevisar={() => revisarDesdePanel(abierta)}
                    revisando={resolverTabla.isPending}
                  />
                ) : null
              })()}

              <nav
                aria-label="Paginacion"
                className="flex items-center justify-between gap-3 text-sm"
              >
                <button
                  type="button"
                  aria-label="Pagina anterior"
                  disabled={pagina <= 1}
                  onClick={() => irAPagina(pagina - 1)}
                  className={CLASE_PAGINACION}
                >
                  Anterior
                </button>
                <span className="text-tinta-apagada">
                  Pagina {pagina} de {paginas}
                </span>
                <button
                  type="button"
                  aria-label="Pagina siguiente"
                  disabled={pagina >= paginas}
                  onClick={() => irAPagina(pagina + 1)}
                  className={CLASE_PAGINACION}
                >
                  Siguiente
                </button>
              </nav>
            </>
          )}

          <BarraSeleccion
            marcados={marcadas.length}
            sinBono={faltanBono}
            fecha={filtros.fecha}
            revisando={resolverTabla.isPending}
            onRevisar={revisarMarcados}
            onSoltar={soltarMarcados}
          />

          {resolverTabla.isError ? <EstadoError error={resolverTabla.error} /> : null}
        </section>
      ) : null}

      {/* Modos individual/masivo: el conmutador de arriba cambia entre ellos. */}
      {filtros.modo === 'individual' ? (
        <ModoIndividual
          q={filtros.q}
          cliente={filtros.cliente}
          onBuscar={cambiarBusqueda}
          onElegirCliente={(id) => {
            elegirCliente(id)
            reiniciar([])
          }}
          onFilas={reiniciar}
        />
      ) : null}

      {filtros.modo === 'masivo' ? <ModoMasivo onFilas={reiniciar} /> : null}

      {/* El modal de revisión y el de RESULTADO nunca se muestran a la vez: al
          publicar, `publicar.data` abre el de resultado y oculta este, aunque
          queden filas reintentables (antes se apilaban dos modales y el de
          revisión quedaba atrapado detrás → solo cerraba con Escape). */}
      {filas.length > 0 && !publicar.data ? (
        <Modal
          titulo="Revisar y publicar"
          onCerrar={() => reiniciar([])}
          anchoMax="max-w-5xl"
        >
          <div className="flex items-start justify-between gap-3 border-b border-borde px-5 py-4">
            <div>
              <h2 className="text-base font-semibold text-tinta">Revisar y publicar</h2>
              <p className="mt-0.5 text-xs text-tinta-apagada">
                {listas.length} {listas.length === 1 ? 'cliente listo' : 'clientes listos'}
                {filtros.fecha ? ` · fecha de reparto ${filtros.fecha}` : ''}
              </p>
            </div>
            <button
              type="button"
              onClick={() => reiniciar([])}
              aria-label="Cerrar la revisión"
              className="rounded-md px-2 py-1 text-sm text-tinta-2 ring-1 ring-borde hover:bg-lienzo"
            >
              ✕
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            <section aria-label="Revision del pedido" className="flex min-w-0 flex-col gap-4">
              {filtros.modo === 'individual' ? null : (
                <TablaResolucion
                  filas={filas}
                  fichas={fichas}
                  precios={precios}
                  anadidas={anadidas}
                  historiales={historiales}
                  seleccionada={seleccionada}
                  onSeleccionar={seleccionar}
                  onElegirFicha={elegirFicha}
                  direccionesPorCliente={direccionesPorFila}
                  onElegirEntrega={(contactId, dirs) =>
                    setDireccionesPorFila((prev) => ({ ...prev, [contactId]: dirs }))
                  }
                  etiqueta={enTabla ? 'Revision del reparto' : 'Resolucion de la lista'}
                />
              )}

              {detalle ? (
                <div className="flex flex-col gap-3">
                  {filtros.modo === 'individual' && detalle.resultado ? (
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-medium text-tinta-2">Ficha del cliente</span>
                      {(detalle.resultado.candidatos ?? []).length > 0 ? (
                        <SelectorFicha
                          cliente={detalle.resultado.cliente?.nombre ?? detalle.nif}
                          candidatos={detalle.resultado.candidatos ?? []}
                          elegida={partnerElegido(detalle, fichas)}
                          onElegir={(id) => elegirFicha(detalle.clave, id)}
                        />
                      ) : detalle.resultado.ficha ? (
                        <FichaResuelta ficha={detalle.resultado.ficha} />
                      ) : (
                        <span className="text-sm text-tinta-apagada">
                          Este cliente no está en la base de datos.
                        </span>
                      )}
                    </div>
                  ) : null}

                  {lineasDe(detalle, precios, anadidas, cantidades, bonosLinea).length > 0 || detalle.resultado?.cliente ? (
                    <DetallePedido
                      lineas={lineasDe(detalle, precios, anadidas, cantidades, bonosLinea)}
                      catalogo={catalogo.data?.items}
                      anadidas={anadidas[detalle.clave] ?? []}
                      onCambiarAnadidas={(lineas) =>
                        setAnadidas((previas) => ({ ...previas, [detalle.clave]: lineas }))
                      }
                      historial={historiales.get(detalle.resultado?.cliente?.contact_id ?? '')}
                      onCambiarPrecio={(indice, precio) =>
                        setPrecios((previos) => {
                          const clave = clavePrecio(detalle.clave, indice)
                          if (precio === null) {
                            const { [clave]: _, ...resto } = previos
                            return resto
                          }
                          return { ...previos, [clave]: precio }
                        })
                      }
                      onCambiarCantidad={(indice, cantidad) =>
                        setCantidades((previos) => {
                          const clave = clavePrecio(detalle.clave, indice)
                          if (cantidad === null) {
                            const { [clave]: _, ...resto } = previos
                            return resto
                          }
                          return { ...previos, [clave]: cantidad }
                        })
                      }
                      onCambiarBono={(indice, bono) =>
                        setBonosLinea((previos) => {
                          const clave = clavePrecio(detalle.clave, indice)
                          if (bono === null) {
                            const { [clave]: _, ...resto } = previos
                            return resto
                          }
                          return { ...previos, [clave]: bono }
                        })
                      }
                      eliminadas={eliminadas[detalle.clave] ?? []}
                      onAlternarEliminada={(indice) =>
                        setEliminadas((previas) => {
                          const actuales = previas[detalle.clave] ?? []
                          const siguiente = actuales.includes(indice)
                            ? actuales.filter((i) => i !== indice)
                            : [...actuales, indice]
                          if (siguiente.length === 0) {
                            const { [detalle.clave]: _, ...resto } = previas
                            return resto
                          }
                          return { ...previas, [detalle.clave]: siguiente }
                        })
                      }
                    />
                  ) : (
                    <p className="rounded-lg border border-critico-anillo bg-critico-fondo px-3 py-2 text-sm text-critico-tinta">
                      {motivoNoPublicable(detalle, fichas, anadidas) ?? 'Esta fila no tiene pedido.'}
                    </p>
                  )}
                </div>
              ) : null}
            </section>
          </div>

          <div className="border-t border-borde px-5 py-4">
            <BarraPublicar
              clientes={listas.length}
              excluidos={filas.length - listas.length}
              fecha={filtros.fecha}
              onFecha={cambiarFecha}
              publicando={publicar.isPending}
              onPublicar={() => publicarFilas(listas)}
            />
            {/* El error de publicar se muestra AQUÍ DENTRO del modal (antes salía
                suelto al final de la página, tapado por este modal → parecía que
                "no pasaba nada"). */}
            {avisoPublicar ? (
              <p role="alert" className="mt-3 rounded-lg border border-atencion-anillo bg-atencion-fondo px-3 py-2 text-sm text-atencion-tinta">
                {avisoPublicar}
              </p>
            ) : null}
            {publicar.isError ? (
              <div className="mt-3">
                <EstadoError error={publicar.error} />
              </div>
            ) : null}
          </div>
        </Modal>
      ) : null}

      {publicar.data ? (
        <Modal
          titulo="Resultado de la publicación"
          onCerrar={() => publicar.reset()}
          anchoMax="max-w-2xl"
        >
          <div className="px-5 py-4">
            <PanelResultados
              respuesta={publicar.data}
              reintentando={publicar.isPending}
              onReintentar={reintentar}
            />
          </div>
        </Modal>
      ) : null}

      {/* Perfil 360º del cliente, abierto desde su nombre en la tabla. */}
      <PerfilCliente contactId={perfilId} onCerrar={() => setPerfilId(null)} />
    </div>
  )
}
